import type React from "react";
import { useEffect, useMemo, useRef } from "react";
import { useForm } from "react-hook-form";
import type { UseFormRegisterReturn } from "react-hook-form";
import { ExternalLink, ArrowRight, Loader2 } from "lucide-react";
import type { ProfileEditableFieldType } from "@shared/definitions/profile-user-edit-locked-fields-feature.definition";
import {
  STORAGE_FILE_TYPE_CONFIGS,
  StorageFileType,
} from "@shared/schemas/storage-file.schemas";
import type { EventParticipantSelfView } from "@shared/schemas/event-participant-views.schemas";
import type { UpdateParticipantBody } from "@shared/schemas/event-participant-write.schemas";
import type { ExternalEditConfig } from "../controllers/useProfileEditController";
import { Input } from "@planta-v2/pds-ui/input";
import { Textarea } from "@planta-v2/pds-ui/textarea";
import { Label } from "@planta-v2/pds-ui/label";
import { Button } from "@planta-v2/pds-ui/button";
import { Avatar, AvatarFallback } from "@planta-v2/pds-ui/avatar";
import { PlantaAvatarImage } from "@planta-v2/pds-ui/planta-avatar-image";
import { StringUtils } from "@frontend/shared/presentation/utils/string.utils";
import { i18n } from "@frontend/i18n";
import { LocalizedTextUtils } from "@shared/utils/localized-text.utils";
import { resolveParticipantDisplayName } from "@shared/utils/participant-name.utils";
import type { LocalizedText } from "@shared/schemas/localized-text.schemas";

interface ProfileEditFormProps {
  readonly participant: EventParticipantSelfView;
  readonly lockedFields: readonly ProfileEditableFieldType[];
  readonly instructions: string | null;
  readonly externalEdit: ExternalEditConfig | null;
  readonly isSaving: boolean;
  readonly isUploadingPhoto: boolean;
  readonly isRemovingPhoto: boolean;
  readonly canNavigateToDirectory: boolean;
  readonly onSave: (data: UpdateParticipantBody) => Promise<void>;
  readonly onUploadPhoto: (file: File) => Promise<void>;
  readonly onRemovePhoto: () => Promise<void>;
  /** The add-a-photo screen: where a first photo is explained before it is asked for. */
  readonly onOpenPhotoPage: () => void;
  readonly onNavigateToDirectory: () => void;
}

type NameModeType = "firstLast" | "fullName";

interface ProfileFormValues {
  readonly firstName: string;
  readonly lastName: string;
  readonly fullName: string;
  readonly nickName: string;
  readonly title: string;
  readonly bio: string;
  readonly summary: string;
}

interface FieldLockState {
  readonly firstName: boolean;
  readonly lastName: boolean;
  readonly fullName: boolean;
  readonly title: boolean;
  readonly bio: boolean;
  readonly summary: boolean;
}

type IdentityValues = {
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly fullName: string | null;
  readonly nickName: string | null;
};

const AVATAR_ACCEPT =
  STORAGE_FILE_TYPE_CONFIGS[StorageFileType.AVATAR_IMAGE].allowedMimeTypes.join(
    ",",
  );

// The canon renders each field as a label-above-value row on a hairline, so the
// pds pill box is stripped off the control and the row owns the chrome.
const ROW_CSSCLASS =
  "flex flex-col gap-1 border-b border-foreground/10 py-3 transition-colors last:border-b-0 focus-within:border-foreground/40";
const ROW_LABEL_CSSCLASS =
  "text-pds-micro font-medium uppercase leading-none tracking-[0.08em] text-muted-foreground";
const ROW_VALUE_CSSCLASS =
  "h-auto min-h-0 resize-none rounded-none border-0 bg-transparent px-0 py-0 leading-snug shadow-none focus-visible:border-0 focus-visible:ring-0 disabled:bg-transparent disabled:text-foreground/50 dark:bg-transparent";

function isFieldLocked(
  fieldName: ProfileEditableFieldType,
  lockedFields: readonly ProfileEditableFieldType[],
): boolean {
  return lockedFields.includes(fieldName);
}

function resolveLocalizedForViewer(value: LocalizedText | undefined): string {
  return value !== undefined
    ? LocalizedTextUtils.resolve(value, i18n.getLocale())
    : "";
}

function getNameMode(identity: IdentityValues): NameModeType {
  const hasFirstOrLast =
    identity.firstName !== null || identity.lastName !== null;
  if (hasFirstOrLast) return "firstLast";
  if (identity.fullName !== null) return "fullName";
  return "firstLast";
}

function hasAnyLockedField(
  locked: FieldLockState,
  nameMode: NameModeType,
): boolean {
  const hasLockedName =
    nameMode === "firstLast"
      ? locked.firstName || locked.lastName
      : locked.fullName;
  return hasLockedName || locked.title || locked.bio || locked.summary;
}

function getShowNickName(identity: IdentityValues): boolean {
  return (
    identity.nickName !== null &&
    identity.firstName === null &&
    identity.lastName === null &&
    identity.fullName === null
  );
}

function FieldRow({
  label,
  registration,
  locked,
  multiline,
  placeholder,
}: {
  readonly label: string;
  readonly registration: UseFormRegisterReturn;
  readonly locked: boolean;
  readonly multiline?: boolean;
  readonly placeholder?: string;
}): React.JSX.Element {
  const fieldId = `profile-${registration.name}`;
  const resolvedPlaceholder = locked
    ? i18n.t("user.profile.form.notProvided")
    : placeholder;
  return (
    <div className={ROW_CSSCLASS}>
      <Label htmlFor={fieldId} className={ROW_LABEL_CSSCLASS}>
        {label}
      </Label>
      {multiline === true ? (
        <Textarea
          id={fieldId}
          {...registration}
          disabled={locked}
          rows={1}
          placeholder={resolvedPlaceholder}
          className={ROW_VALUE_CSSCLASS}
        />
      ) : (
        <Input
          id={fieldId}
          {...registration}
          disabled={locked}
          placeholder={resolvedPlaceholder}
          className={ROW_VALUE_CSSCLASS}
        />
      )}
    </div>
  );
}

function ReadOnlyRow({
  label,
  value,
}: {
  readonly label: string;
  readonly value: string;
}): React.JSX.Element {
  return (
    <div className={ROW_CSSCLASS}>
      <p className={ROW_LABEL_CSSCLASS}>{label}</p>
      <p className="text-base leading-snug text-foreground md:text-sm">
        {value}
      </p>
    </div>
  );
}

function ExternalEditSection({
  externalEdit,
  instructions,
}: {
  readonly externalEdit: ExternalEditConfig | null;
  readonly instructions: string | null;
}): React.JSX.Element | null {
  if (externalEdit === null && instructions === null) {
    return null;
  }

  return (
    <div className="flex w-full flex-col gap-2 pt-4">
      {externalEdit !== null && (
        <Button
          type="button"
          variant="outline"
          className="w-full font-semibold"
          asChild
        >
          <a href={externalEdit.url} target="_blank" rel="noopener noreferrer">
            {externalEdit.text}
            <ExternalLink className="h-4 w-4" />
          </a>
        </Button>
      )}
      {instructions !== null && (
        <p className="w-full text-xs leading-snug text-muted-foreground">
          {instructions}
        </p>
      )}
    </div>
  );
}

function ProfilePhotoSection({
  photoUrl,
  displayName,
  isUploading,
  isRemoving,
  onUploadPhoto,
  onRemovePhoto,
  onOpenPhotoPage,
}: {
  readonly photoUrl: string | null | undefined;
  readonly displayName: string;
  readonly isUploading: boolean;
  readonly isRemoving: boolean;
  readonly onUploadPhoto: (file: File) => Promise<void>;
  readonly onRemovePhoto: () => Promise<void>;
  readonly onOpenPhotoPage: () => void;
}): React.JSX.Element {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const initials = StringUtils.getInitials(displayName);
  const hasPhoto =
    photoUrl !== null && photoUrl !== undefined && photoUrl !== "";

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file !== undefined) {
      void onUploadPhoto(file);
    }

    if (fileInputRef.current !== null) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="flex items-center gap-4 py-4">
      <Avatar key={photoUrl ?? "no-photo"} className="size-16">
        {hasPhoto && !isRemoving ? (
          <PlantaAvatarImage src={photoUrl} alt={displayName} />
        ) : null}
        <AvatarFallback className="bg-muted-foreground/20 font-serif text-lg font-normal text-foreground/75">
          {isUploading || isRemoving ? (
            <Loader2
              className="h-6 w-6 animate-spin text-muted-foreground"
              data-testid="photo-upload-spinner"
              aria-label={
                isUploading
                  ? i18n.t("user.profile.form.uploading")
                  : i18n.t("user.profile.form.removing")
              }
            />
          ) : (
            initials
          )}
        </AvatarFallback>
      </Avatar>
      <div className="flex min-w-0 flex-col items-start gap-2">
        <p className="truncate text-lg font-semibold leading-tight text-foreground">
          {displayName}
        </p>
        <div className="flex items-center gap-1">
          <input
            ref={fileInputRef}
            type="file"
            accept={AVATAR_ACCEPT}
            className="hidden"
            onChange={handleFileChange}
            data-testid="photo-file-input"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isUploading || isRemoving}
            onClick={(): void => {
              if (hasPhoto) {
                fileInputRef.current?.click();
              } else {
                onOpenPhotoPage();
              }
            }}
            data-testid="photo-upload-button"
          >
            {isUploading
              ? i18n.t("user.profile.form.uploading")
              : hasPhoto
                ? i18n.t("user.profile.form.changePhoto")
                : i18n.t("user.profile.form.addPhoto")}
          </Button>
          {hasPhoto && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={isUploading || isRemoving}
              onClick={(): void => {
                void onRemovePhoto();
              }}
              data-testid="photo-remove-button"
            >
              {isRemoving
                ? i18n.t("user.profile.form.removing")
                : i18n.t("user.profile.form.removePhoto")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function FieldRows({
  locked,
  nameMode,
  showNickName,
  companyName,
  registerFirstName,
  registerLastName,
  registerFullName,
  registerNickName,
  registerTitle,
  registerBio,
  registerSummary,
}: {
  readonly locked: FieldLockState;
  readonly nameMode: NameModeType;
  readonly showNickName: boolean;
  readonly companyName: string | null;
  readonly registerFirstName: UseFormRegisterReturn;
  readonly registerLastName: UseFormRegisterReturn;
  readonly registerFullName: UseFormRegisterReturn;
  readonly registerNickName: UseFormRegisterReturn;
  readonly registerTitle: UseFormRegisterReturn;
  readonly registerBio: UseFormRegisterReturn;
  readonly registerSummary: UseFormRegisterReturn;
}): React.JSX.Element {
  return (
    <div className="flex flex-col">
      {nameMode === "firstLast" && (
        <FieldRow
          label={i18n.t("user.profile.form.firstName")}
          registration={registerFirstName}
          locked={locked.firstName}
        />
      )}
      {nameMode === "firstLast" && (
        <FieldRow
          label={i18n.t("user.profile.form.lastName")}
          registration={registerLastName}
          locked={locked.lastName}
        />
      )}
      {nameMode === "fullName" && (
        <FieldRow
          label={i18n.t("user.profile.form.fullName")}
          registration={registerFullName}
          locked={locked.fullName}
        />
      )}
      {showNickName && (
        <FieldRow
          label={i18n.t("user.profile.form.nickName")}
          registration={registerNickName}
          locked={false}
        />
      )}
      <FieldRow
        label={i18n.t("user.profile.form.title")}
        registration={registerTitle}
        locked={locked.title}
        placeholder={i18n.t("user.profile.form.titlePlaceholder")}
      />
      {companyName !== null && companyName !== "" && (
        <ReadOnlyRow
          label={i18n.t("user.profile.form.company")}
          value={companyName}
        />
      )}
      <FieldRow
        label={i18n.t("user.profile.form.bio")}
        registration={registerBio}
        locked={locked.bio}
        multiline
        placeholder={i18n.t("user.profile.form.bioPlaceholder")}
      />
      <FieldRow
        label={i18n.t("user.profile.form.summary")}
        registration={registerSummary}
        locked={locked.summary}
        multiline
        placeholder={i18n.t("user.profile.form.summaryPlaceholder")}
      />
    </div>
  );
}

export function ProfileEditForm({
  participant,
  lockedFields,
  instructions,
  externalEdit,
  isSaving,
  isUploadingPhoto,
  isRemovingPhoto,
  canNavigateToDirectory,
  onSave,
  onUploadPhoto,
  onRemovePhoto,
  onOpenPhotoPage,
  onNavigateToDirectory,
}: ProfileEditFormProps): React.JSX.Element {
  const identity = useMemo(
    () => ({
      firstName: participant.firstName.value,
      lastName: participant.lastName.value,
      fullName: participant.fullName.value,
      nickName: participant.nickName.value,
    }),
    [
      participant.firstName.value,
      participant.lastName.value,
      participant.fullName.value,
      participant.nickName.value,
    ],
  );
  const nameMode = useMemo(() => getNameMode(identity), [identity]);
  const showNickName = useMemo(() => getShowNickName(identity), [identity]);

  const displayName = useMemo(
    () => resolveParticipantDisplayName(identity),
    [identity],
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { isDirty, dirtyFields },
  } = useForm<ProfileFormValues>({
    defaultValues: {
      firstName: identity.firstName ?? "",
      lastName: identity.lastName ?? "",
      fullName: identity.fullName ?? "",
      nickName: identity.nickName ?? "",
      title: participant.title?.value ?? "",
      // Submit sends raw string; backend re-detects language to set `lang`.
      bio: resolveLocalizedForViewer(participant.bio?.value),
      summary: resolveLocalizedForViewer(participant.summary?.value),
    },
  });

  useEffect(() => {
    reset({
      firstName: participant.firstName.value ?? "",
      lastName: participant.lastName.value ?? "",
      fullName: participant.fullName.value ?? "",
      nickName: participant.nickName.value ?? "",
      title: participant.title?.value ?? "",
      bio: resolveLocalizedForViewer(participant.bio?.value),
      summary: resolveLocalizedForViewer(participant.summary?.value),
    });
  }, [participant, reset]);

  const locked = useMemo(
    (): FieldLockState => ({
      firstName: isFieldLocked("firstName", lockedFields),
      lastName: isFieldLocked("lastName", lockedFields),
      fullName: isFieldLocked("fullName", lockedFields),
      title: isFieldLocked("title", lockedFields),
      bio: isFieldLocked("bio", lockedFields),
      summary: isFieldLocked("summary", lockedFields),
    }),
    [lockedFields],
  );
  const hasLockedField = hasAnyLockedField(locked, nameMode);

  const onSubmit = handleSubmit(async (data) => {
    const nameFields: Partial<UpdateParticipantBody> =
      nameMode === "firstLast"
        ? {
            ...(!locked.firstName && { firstName: data.firstName }),
            ...(!locked.lastName && { lastName: data.lastName }),
          }
        : { ...(!locked.fullName && { fullName: data.fullName }) };

    const nickNameFields: Partial<UpdateParticipantBody> = showNickName
      ? { nickName: data.nickName }
      : {};

    // Initial bio/summary are resolved to the viewer's locale; submitting them
    // unedited would overwrite canonical content + AI translations.
    const updateData: UpdateParticipantBody = {
      ...nameFields,
      ...nickNameFields,
      ...(!locked.title && { title: data.title }),
      ...(!locked.bio && dirtyFields.bio === true && { bio: data.bio }),
      ...(!locked.summary &&
        dirtyFields.summary === true && { summary: data.summary }),
    };
    await onSave(updateData);
  });

  const registerFirstName = register("firstName");
  const registerLastName = register("lastName");
  const registerFullName = register("fullName");
  const registerNickName = register("nickName");
  const registerTitle = register("title");
  const registerBio = register("bio");
  const registerSummary = register("summary");

  return (
    <form
      onSubmit={(e): void => {
        void onSubmit(e);
      }}
      className="flex flex-col px-4 pb-6"
    >
      <ProfilePhotoSection
        photoUrl={participant.photoUrl?.value ?? null}
        displayName={displayName}
        isUploading={isUploadingPhoto}
        isRemoving={isRemovingPhoto}
        onUploadPhoto={onUploadPhoto}
        onRemovePhoto={onRemovePhoto}
        onOpenPhotoPage={onOpenPhotoPage}
      />
      <p className={`${ROW_LABEL_CSSCLASS} pb-3 pt-4`}>
        {i18n.t("user.profile.form.whatPeopleSee")}
      </p>
      <FieldRows
        locked={locked}
        nameMode={nameMode}
        showNickName={showNickName}
        companyName={participant.companyName}
        registerFirstName={registerFirstName}
        registerLastName={registerLastName}
        registerFullName={registerFullName}
        registerNickName={registerNickName}
        registerTitle={registerTitle}
        registerBio={registerBio}
        registerSummary={registerSummary}
      />
      {hasLockedField && (
        <ExternalEditSection
          externalEdit={externalEdit}
          instructions={instructions}
        />
      )}
      <p className="pt-4 text-xs leading-snug text-muted-foreground">
        {i18n.t("user.profile.form.porterReads")}
      </p>
      <Button
        type="submit"
        size="lg"
        className="mt-4 w-full"
        disabled={!isDirty || isSaving}
      >
        {isSaving
          ? i18n.t("user.profile.form.saving")
          : i18n.t("user.profile.form.save")}
      </Button>
      <Button
        type="button"
        variant="ghost"
        className="mt-1 w-full"
        onClick={onNavigateToDirectory}
        disabled={!canNavigateToDirectory}
        data-testid="open-in-directory-button"
      >
        {i18n.t("user.profile.form.openInDirectory")}
        <ArrowRight className="h-4 w-4" />
      </Button>
    </form>
  );
}
